<template>
  <BaseTemplate app-name="Medical System" page-title="">
    <template #content>
      <div
        class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200"
      >
        <!-- Main Content -->
        <div class="flex-1 flex overflow-hidden">
          <!-- Content Area -->
          <div class="flex-1 overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div class="flex flex-wrap justify-between items-center gap-4">
                <div class="flex items-center gap-3">
                  <h2 class="text-xl font-medium clinic-header">{{ currentView === 'list' ? 'Notificações' : 'Configuração de Notificação' }}</h2>
                  <button
                    v-if="currentView === 'form'"
                    class="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    @click="goToList"
                    aria-label="Voltar para lista"
                  >
                    <ArrowLeftIcon class="w-5 h-5" />
                  </button>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    v-if="currentView === 'list'"
                    class="px-4 py-2 clinic-btn rounded-md transition-colors shadow-sm flex items-center gap-2"
                    @click="openNewNotificationForm"
                  >
                    <PlusIcon class="w-4 h-4" />
                    <span>Nova Notificação</span>
                  </button>
                  <button
                    v-if="currentView === 'form'"
                    class="px-4 py-2 clinic-btn rounded-md transition-colors shadow-sm flex items-center gap-2"
                    @click="saveNotification"
                    :disabled="!isFormValid"
                  >
                    <SaveIcon class="w-4 h-4" />
                    <span>{{ editMode ? 'Atualizar' : 'Salvar' }} Notificação</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Notification List View -->
            <div v-if="currentView === 'list'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700 p-4">
                <!-- Search and Filter -->
                <div class="mb-4 flex flex-wrap gap-4">
                  <div class="flex-1 min-w-[240px]">
                    <div class="relative">
                      <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Buscar notificações..."
                        class="w-full px-3 py-2 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                  <div class="w-full md:w-auto flex gap-2">
                    <div class="relative">
                      <select
                        v-model="filterEvent"
                        class="h-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                      >
                        <option value="">Todos os eventos</option>
                        <option value="patient.created">Paciente Cadastrado</option>
                        <option value="patient.updated">Paciente Atualizado</option>
                        <option value="appointment.created">Consulta Agendada</option>
                        <option value="appointment.updated">Consulta Atualizada</option>
                        <option value="appointment.canceled">Consulta Cancelada</option>
                        <option value="appointment.reminder">Lembrete de Consulta</option>
                        <option value="exam.result">Resultado de Exame</option>
                        <option value="invoice.generated">Fatura Gerada</option>
                        <option value="payment.received">Pagamento Recebido</option>
                      </select>
                      <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                    <button
                      @click="resetFilters"
                      class="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <RefreshCwIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <!-- Notifications Table -->
                <div class="overflow-x-auto">
                  <table class="w-full border-collapse">
                    <thead>
                      <tr class="bg-gray-50 dark:bg-gray-900/50 text-left">
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          ID
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Nome
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Eventos
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Destinatários
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Canais
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Status
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="filteredNotifications.length === 0">
                        <td colspan="7" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                          Nenhuma notificação encontrada
                        </td>
                      </tr>
                      <tr
                        v-for="notification in filteredNotifications"
                        :key="notification.id"
                        class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <td class="px-4 py-3 text-sm">{{ notification.id }}</td>
                        <td class="px-4 py-3">
                          <div class="flex items-center gap-3">
                            <div
                              class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                              :style="{ backgroundColor: getNotificationColor(notification.id) }"
                            >
                              <BellIcon class="w-4 h-4" />
                            </div>
                            <div class="text-sm font-medium">{{ notification.name }}</div>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex flex-wrap gap-1">
                            <span
                              v-for="(event, index) in notification.events.slice(0, 2)"
                              :key="index"
                              class="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                            >
                              {{ getEventLabel(event) }}
                            </span>
                            <span
                              v-if="notification.events.length > 2"
                              class="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                            >
                              +{{ notification.events.length - 2 }}
                            </span>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex flex-wrap gap-1">
                            <span
                              v-for="(recipient, index) in notification.recipients.slice(0, 2)"
                              :key="index"
                              class="px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                            >
                              {{ recipient.name }}
                            </span>
                            <span
                              v-if="notification.recipients.length > 2"
                              class="px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                            >
                              +{{ notification.recipients.length - 2 }}
                            </span>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex gap-1">
                            <span
                              v-if="notification.channels.includes('app')"
                              class="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                              title="Notificação no App"
                            >
                              <BellIcon class="w-3 h-3" />
                            </span>
                            <span
                              v-if="notification.channels.includes('email')"
                              class="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                              title="Email"
                            >
                              <MailIcon class="w-3 h-3" />
                            </span>
                            <span
                              v-if="notification.channels.includes('sms')"
                              class="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                              title="SMS"
                            >
                              <SmartphoneIcon class="w-3 h-3" />
                            </span>
                            <span
                              v-if="notification.channels.includes('whatsapp')"
                              class="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                              title="WhatsApp"
                            >
                              <MessageCircleIcon class="w-3 h-3" />
                            </span>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <span
                            class="px-2 py-1 text-xs rounded-full"
                            :class="{
                              'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400': notification.active,
                              'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400': !notification.active
                            }"
                          >
                            {{ notification.active ? 'Ativo' : 'Inativo' }}
                          </span>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex gap-2">
                            <button
                              @click="editNotification(notification)"
                              class="p-1 text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400"
                              title="Editar"
                            >
                              <EditIcon class="w-4 h-4" />
                            </button>
                            <button
                              @click="confirmDeleteNotification(notification)"
                              class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                              title="Excluir"
                            >
                              <TrashIcon class="w-4 h-4" />
                            </button>
                            <button
                              @click="testNotification(notification)"
                              class="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                              title="Testar Notificação"
                            >
                              <SendIcon class="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div class="mt-4 flex justify-between items-center">
                  <div class="text-sm text-gray-500 dark:text-gray-400">
                    Mostrando {{ filteredNotifications.length }} de {{ notifications.length }} notificações
                  </div>
                  <div class="flex gap-2">
                    <button
                      class="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                      disabled
                    >
                      Anterior
                    </button>
                    <button
                      class="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                      disabled
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notification Form View with Tabs -->
            <div v-if="currentView === 'form'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700">
                <!-- Tabs Navigation -->
                <div class="border-b border-gray-200 dark:border-gray-700">
                  <div class="flex overflow-x-auto">
                    <button
                      v-for="tab in tabs"
                      :key="tab.id"
                      @click="activeTab = tab.id"
                      class="px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none"
                      :class="activeTab === tab.id ? 'clinic-tab-active' : 'clinic-tab-inactive'"
                    >
                      <div class="flex items-center gap-2">
                        <component :is="tab.icon" class="w-4 h-4" />
                        {{ tab.name }}
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Tab Content -->
                <div class="p-6">
                  <!-- Basic Information Tab -->
                  <div v-if="activeTab === 'basic'" class="space-y-4">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <!-- ID da Notificação -->
                      <div>
                        <label class="block text-sm font-medium mb-1">ID da Notificação</label>
                        <div class="relative">
                          <input
                            type="text"
                            v-model="currentNotification.id"
                            placeholder="Gerado automaticamente"
                            disabled
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <!-- Nome da Notificação -->
                      <div class="md:col-span-2">
                        <label for="name" class="block text-sm font-medium mb-1">Nome da Notificação*</label>
                        <input
                          id="name"
                          type="text"
                          v-model="currentNotification.name"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label for="description" class="block text-sm font-medium mb-1">Descrição</label>
                      <textarea
                        id="description"
                        v-model="currentNotification.description"
                        rows="2"
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      ></textarea>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- Status -->
                      <div>
                        <label for="active" class="block text-sm font-medium mb-1">Status</label>
                        <div class="relative">
                          <select
                            id="active"
                            v-model="currentNotification.active"
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                          >
                            <option :value="true">Ativo</option>
                            <option :value="false">Inativo</option>
                          </select>
                          <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                          </div>
                        </div>
                      </div>

                      <!-- Prioridade -->
                      <div>
                        <label for="priority" class="block text-sm font-medium mb-1">Prioridade</label>
                        <div class="relative">
                          <select
                            id="priority"
                            v-model="currentNotification.priority"
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                          >
                            <option value="low">Baixa</option>
                            <option value="normal">Normal</option>
                            <option value="high">Alta</option>
                            <option value="urgent">Urgente</option>
                          </select>
                          <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-medium mb-2">Canais de Notificação*</label>
                      <div class="flex flex-wrap gap-3">
                        <label class="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            v-model="currentNotification.channels"
                            value="app"
                            class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <span class="text-sm flex items-center gap-1">
                            <BellIcon class="w-4 h-4" />
                            Notificação no App
                          </span>
                        </label>
                        <label class="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            v-model="currentNotification.channels"
                            value="email"
                            class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <span class="text-sm flex items-center gap-1">
                            <MailIcon class="w-4 h-4" />
                            Email
                          </span>
                        </label>
                        <label class="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            v-model="currentNotification.channels"
                            value="sms"
                            class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <span class="text-sm flex items-center gap-1">
                            <SmartphoneIcon class="w-4 h-4" />
                            SMS
                          </span>
                        </label>
                        <label class="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            v-model="currentNotification.channels"
                            value="whatsapp"
                            class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <span class="text-sm flex items-center gap-1">
                            <MessageCircleIcon class="w-4 h-4" />
                            WhatsApp
                          </span>
                        </label>
                      </div>
                      <p 
                        v-if="currentNotification.channels.length === 0" 
                        class="text-sm text-red-500 mt-2"
                      >
                        Selecione pelo menos um canal de notificação
                      </p>
                    </div>
                  </div>

                  <!-- Events Tab -->
                  <div v-if="activeTab === 'events'" class="space-y-4">
                    <div class="mb-4">
                      <div class="flex justify-between items-center mb-2">
                        <label class="block text-sm font-medium">Eventos que Disparam a Notificação*</label>
                        <div class="flex gap-2">
                          <button
                            @click="selectAllEvents"
                            class="text-xs text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400"
                          >
                            Selecionar Todos
                          </button>
                          <span class="text-gray-500">|</span>
                          <button
                            @click="deselectAllEvents"
                            class="text-xs text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400"
                          >
                            Limpar Seleção
                          </button>
                        </div>
                      </div>
                      <div class="space-y-3 bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md border border-gray-200 dark:border-gray-700">
                        <div class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Pacientes</div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="patient.created"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Paciente Cadastrado</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="patient.updated"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Paciente Atualizado</span>
                          </label>
                        </div>

                        <div class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 mt-4">Consultas</div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="appointment.created"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Consulta Agendada</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="appointment.updated"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Consulta Atualizada</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="appointment.canceled"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Consulta Cancelada</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="appointment.reminder"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Lembrete de Consulta</span>
                          </label>
                        </div>

                        <div class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 mt-4">Exames</div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="exam.result"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Resultado de Exame</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="exam.scheduled"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Exame Agendado</span>
                          </label>
                        </div>

                        <div class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 mt-4">Financeiro</div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="invoice.generated"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Fatura Gerada</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="payment.received"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Pagamento Recebido</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentNotification.events"
                              value="invoice.overdue"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Fatura Vencida</span>
                          </label>
                        </div>
                      </div>
                      <p 
                        v-if="currentNotification.events.length === 0" 
                        class="text-sm text-red-500 mt-2"
                      >
                        Selecione pelo menos um evento para esta notificação
                      </p>
                    </div>
                  </div>

                  <!-- Recipients Tab -->
                  <div v-if="activeTab === 'recipients'" class="space-y-4">
                    <div class="mb-4">
                      <div class="flex justify-between items-center mb-2">
                        <label class="block text-sm font-medium">Destinatários*</label>
                        <button
                          @click="showRecipientModal = true"
                          class="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 flex items-center gap-1"
                        >
                          <PlusCircleIcon class="w-4 h-4" />
                          <span>Adicionar Destinatário</span>
                        </button>
                      </div>
                      
                      <div 
                        v-if="currentNotification.recipients.length === 0" 
                        class="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md border border-gray-200 dark:border-gray-700 text-center text-gray-500 dark:text-gray-400"
                      >
                        <UserIcon class="w-8 h-8 mx-auto mb-2 opacity-40" />
                        <p>Nenhum destinatário selecionado</p>
                        <p class="text-sm mt-1">Clique em "Adicionar Destinatário" para selecionar usuários ou grupos</p>
                      </div>
                      
                      <div 
                        v-else 
                        class="space-y-2 bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md border border-gray-200 dark:border-gray-700"
                      >
                        <div 
                          v-for="(recipient, index) in currentNotification.recipients" 
                          :key="index"
                          class="flex justify-between items-center p-2 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700"
                        >
                          <div class="flex items-center gap-3">
                            <div
                              class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                              :class="{
                                'bg-blue-500': recipient.type === 'user',
                                'bg-purple-500': recipient.type === 'group'
                              }"
                            >
                              <UserIcon v-if="recipient.type === 'user'" class="w-4 h-4" />
                              <UsersIcon v-else class="w-4 h-4" />
                            </div>
                            <div>
                              <div class="text-sm font-medium">{{ recipient.name }}</div>
                              <div class="text-xs text-gray-500 dark:text-gray-400">
                                {{ recipient.type === 'user' ? recipient.email : `${recipient.userCount} usuários` }}
                              </div>
                            </div>
                          </div>
                          <button
                            @click="removeRecipient(index)"
                            class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                            title="Remover"
                          >
                            <XIcon class="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      
                      <p 
                        v-if="currentNotification.recipients.length === 0" 
                        class="text-sm text-red-500 mt-2"
                      >
                        Adicione pelo menos um destinatário para esta notificação
                      </p>
                    </div>
                  </div>

                  <!-- Content Tab -->
                  <div v-if="activeTab === 'content'" class="space-y-4">
                    <div>
                      <label for="subject" class="block text-sm font-medium mb-1">Assunto*</label>
                      <input
                        id="subject"
                        type="text"
                        v-model="currentNotification.content.subject"
                        placeholder="Assunto da notificação"
                        required
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Você pode usar variáveis como {nome}, {data}, {id} que serão substituídas pelos valores reais
                      </p>
                    </div>

                    <div>
                      <label for="message" class="block text-sm font-medium mb-1">Mensagem*</label>
                      <textarea
                        id="message"
                        v-model="currentNotification.content.message"
                        rows="5"
                        placeholder="Conteúdo da mensagem"
                        required
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      ></textarea>
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Você pode usar variáveis como {nome}, {data}, {id} que serão substituídas pelos valores reais
                      </p>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="actionText" class="block text-sm font-medium mb-1">Texto do Botão de Ação (opcional)</label>
                        <input
                          id="actionText"
                          type="text"
                          v-model="currentNotification.content.actionText"
                          placeholder="Ex: Ver Detalhes"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="actionUrl" class="block text-sm font-medium mb-1">URL da Ação (opcional)</label>
                        <input
                          id="actionUrl"
                          type="text"
                          v-model="currentNotification.content.actionUrl"
                          placeholder="Ex: /pacientes/{id}"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="mt-6">
                      <h3 class="text-sm font-medium mb-3">Prévia da Notificação</h3>
                      <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <div class="flex items-start gap-3">
                          <div
                            class="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 bg-emerald-500"
                          >
                            <BellIcon class="w-5 h-5" />
                          </div>
                          <div class="flex-1">
                            <div class="font-medium">{{ currentNotification.content.subject || 'Assunto da notificação' }}</div>
                            <div class="text-sm text-gray-600 dark:text-gray-300 mt-1">
                              {{ currentNotification.content.message || 'Conteúdo da mensagem...' }}
                            </div>
                            <div v-if="currentNotification.content.actionText" class="mt-3">
                              <button class="px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs rounded">
                                {{ currentNotification.content.actionText }}
                              </button>
                            </div>
                            <div class="text-xs text-gray-500 dark:text-gray-400 mt-2">Agora mesmo</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- History Tab -->
                  <div v-if="activeTab === 'history'" class="space-y-4">
                    <div 
                      v-if="!editMode || !currentNotification.history || currentNotification.history.length === 0" 
                      class="text-center py-8 text-gray-500 dark:text-gray-400"
                    >
                      <ClockIcon class="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p>Não há histórico disponível para esta notificação.</p>
                      <p class="text-sm mt-1" v-if="!editMode">Salve a notificação primeiro para visualizar o histórico.</p>
                    </div>

                    <div v-else class="space-y-4">
                      <div class="flex justify-between items-center mb-2">
                        <h3 class="text-sm font-medium">Últimas Notificações Enviadas</h3>
                        <button
                          @click="clearHistory"
                          class="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex items-center gap-1"
                        >
                          <TrashIcon class="w-3 h-3" />
                          <span>Limpar Histórico</span>
                        </button>
                      </div>

                      <div class="space-y-3">
                        <div 
                          v-for="(entry, index) in currentNotification.history" 
                          :key="index" 
                          class="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-md border border-gray-200 dark:border-gray-700"
                        >
                          <div class="flex justify-between items-start mb-2">
                            <div class="flex items-center gap-2">
                              <span
                                class="w-2 h-2 rounded-full"
                                :class="{
                                  'bg-emerald-500': entry.status === 'delivered',
                                  'bg-yellow-500': entry.status === 'pending',
                                  'bg-red-500': entry.status === 'failed'
                                }"
                              ></span>
                              <span 
                                class="font-medium"
                                :class="{
                                  'text-emerald-600 dark:text-emerald-400': entry.status === 'delivered',
                                  'text-yellow-600 dark:text-yellow-400': entry.status === 'pending',
                                  'text-red-600 dark:text-red-400': entry.status === 'failed'
                                }"
                              >
                                {{ getStatusLabel(entry.status) }}
                              </span>
                              <span class="text-sm text-gray-500 dark:text-gray-400">{{ getEventLabel(entry.event) }}</span>
                            </div>
                            <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatDate(entry.timestamp) }}</span>
                          </div>
                          <div class="text-xs text-gray-600 dark:text-gray-300 mt-1">
                            <div><span class="font-medium">Destinatário:</span> {{ entry.recipient }}</div>
                            <div><span class="font-medium">Canal:</span> {{ getChannelLabel(entry.channel) }}</div>
                            <div v-if="entry.error"><span class="font-medium">Erro:</span> {{ entry.error }}</div>
                          </div>
                          <div 
                            v-if="expandedHistoryEntry === index" 
                            class="mt-2 text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700"
                          >
                            <div><span class="font-medium">Assunto:</span> {{ entry.subject }}</div>
                            <div class="mt-1"><span class="font-medium">Mensagem:</span> {{ entry.message }}</div>
                          </div>
                          <button
                            @click="toggleHistoryEntry(index)"
                            class="text-xs text-emerald-600 dark:text-emerald-400 mt-2 flex items-center gap-1"
                          >
                            <span>{{ expandedHistoryEntry === index ? 'Ocultar Detalhes' : 'Ver Detalhes' }}</span>
                            <ChevronDownIcon v-if="expandedHistoryEntry !== index" class="w-3 h-3" />
                            <ChevronUpIcon v-else class="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Form Actions -->
                  <div class="mt-8 flex justify-between items-center">
                    <div class="flex gap-2">

                    </div>
                    <div class="flex gap-3">
                      <button
                        class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                        @click="goToList"
                      >
                        Cancelar
                      </button>
                      <button
                        class="px-6 py-2 clinic-btn rounded-md transition-colors shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                        @click="saveNotification"
                        :disabled="!isFormValid"
                      >
                        {{ editMode ? 'Atualizar' : 'Salvar' }} Notificação
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Success Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showSuccessModal"
              @click.self="closeSuccessModal"
            >
              <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn"
              >
                <div class="p-6 text-center">
                  <div
                    class="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto mb-4"
                  >
                    <CheckIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2">{{ editMode ? 'Notificação Atualizada' : 'Notificação Cadastrada' }}</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6">
                    {{ editMode ? 'A notificação foi atualizada com sucesso.' : 'A notificação foi cadastrada com sucesso no sistema.' }}
                  </p>
                  <div class="flex justify-center gap-3">
                    <button
                      class="px-4 py-2 clinic-btn rounded-md transition-colors shadow-sm"
                      @click="closeSuccessModal"
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showDeleteModal"
              @click.self="showDeleteModal = false"
            >
              <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn"
              >
                <div class="p-6">
                  <div
                    class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mx-auto mb-4"
                  >
                    <AlertTriangleIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2 text-center">Confirmar Exclusão</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 text-center">
                    Tem certeza que deseja excluir a notificação <span class="font-medium">{{ notificationToDelete?.name }}</span>?
                    <br />Esta ação não pode ser desfeita.
                  </p>
                  <div class="flex justify-end gap-3">
                    <button
                      class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                      @click="showDeleteModal = false"
                    >
                      Cancelar
                    </button>
                    <button
                      class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors shadow-sm"
                      @click="deleteNotification"
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Test Notification Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showTestModal"
              @click.self="showTestModal = false"
            >
              <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fadeIn"
              >
                <div class="p-6">
                  <h3 class="text-xl font-medium mb-4">Testar Notificação</h3>
                  
                  <div v-if="testStatus === 'preparing'" class="space-y-4">
                    <div>
                      <label for="testEvent" class="block text-sm font-medium mb-1">Evento</label>
                      <div class="relative">
                        <select
                          id="testEvent"
                          v-model="testEvent"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option v-for="event in notificationToTest.events" :key="event" :value="event">
                            {{ getEventLabel(event) }}
                          </option>
                        </select>
                        <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label for="testRecipient" class="block text-sm font-medium mb-1">Destinatário de Teste</label>
                      <div class="relative">
                        <select
                          id="testRecipient"
                          v-model="testRecipient"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option v-for="recipient in availableTestRecipients" :key="recipient.id" :value="recipient.id">
                            {{ recipient.name }} ({{ recipient.email }})
                          </option>
                        </select>
                        <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label for="testChannel" class="block text-sm font-medium mb-1">Canal</label>
                      <div class="relative">
                        <select
                          id="testChannel"
                          v-model="testChannel"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option v-for="channel in notificationToTest.channels" :key="channel" :value="channel">
                            {{ getChannelLabel(channel) }}
                          </option>
                        </select>
                        <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                        </div>
                      </div>
                    </div>
                    
                    <div class="flex justify-end gap-3 mt-6">
                      <button
                        class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                        @click="showTestModal = false"
                      >
                        Cancelar
                      </button>
                      <button
                        class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors shadow-sm"
                        @click="executeTestNotification"
                      >
                        Enviar Notificação de Teste
                      </button>
                    </div>
                  </div>
                  
                  <div v-if="testStatus === 'loading'" class="py-8 text-center">
                    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
                    <p class="mt-4 text-gray-600 dark:text-gray-400">Enviando notificação...</p>
                  </div>
                  
                  <div v-if="testStatus === 'success'" class="py-4">
                    <div
                      class="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto mb-4"
                    >
                      <CheckIcon class="w-8 h-8" />
                    </div>
                    <h4 class="text-lg font-medium text-center mb-2">Notificação Enviada com Sucesso</h4>
                    <div class="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md border border-gray-200 dark:border-gray-700 mt-4">
                      <div class="flex justify-between mb-2">
                        <span class="text-sm font-medium">Evento:</span>
                        <span class="text-sm">{{ getEventLabel(testEvent) }}</span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span class="text-sm font-medium">Destinatário:</span>
                        <span class="text-sm">{{ getTestRecipientName() }}</span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span class="text-sm font-medium">Canal:</span>
                        <span class="text-sm">{{ getChannelLabel(testChannel) }}</span>
                      </div>
                      <div class="mt-3">
                        <div class="text-sm font-medium mb-1">Conteúdo:</div>
                        <div class="bg-white dark:bg-gray-800 p-3 rounded font-mono text-xs overflow-x-auto">
                          <div><span class="font-medium">Assunto:</span> {{ notificationToTest.content.subject }}</div>
                          <div class="mt-1"><span class="font-medium">Mensagem:</span> {{ notificationToTest.content.message }}</div>
                        </div>
                      </div>
                    </div>
                    <div class="flex justify-center mt-6">
                      <button
                        class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors shadow-sm"
                        @click="showTestModal = false"
                      >
                        Fechar
                      </button>
                    </div>
                  </div>
                  
                  <div v-if="testStatus === 'error'" class="py-4">
                    <div
                      class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mx-auto mb-4"
                    >
                      <AlertCircleIcon class="w-8 h-8" />
                    </div>
                    <h4 class="text-lg font-medium text-center mb-2">Falha ao Enviar Notificação</h4>
                    <div class="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md border border-gray-200 dark:border-gray-700 mt-4">
                      <div class="text-red-600 dark:text-red-400 text-sm font-medium mb-2">
                        {{ testResult.error || 'Erro ao enviar a notificação' }}
                      </div>
                      <div class="flex justify-between mb-2">
                        <span class="text-sm font-medium">Evento:</span>
                        <span class="text-sm">{{ getEventLabel(testEvent) }}</span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span class="text-sm font-medium">Destinatário:</span>
                        <span class="text-sm">{{ getTestRecipientName() }}</span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span class="text-sm font-medium">Canal:</span>
                        <span class="text-sm">{{ getChannelLabel(testChannel) }}</span>
                      </div>
                    </div>
                    <div class="flex justify-center mt-6 gap-3">
                      <button
                        class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                        @click="showTestModal = false"
                      >
                        Fechar
                      </button>
                      <button
                        class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors shadow-sm"
                        @click="testStatus = 'preparing'"
                      >
                        Tentar Novamente
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add Recipient Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showRecipientModal"
              @click.self="showRecipientModal = false"
            >
              <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fadeIn"
              >
                <div class="p-6">
                  <h3 class="text-xl font-medium mb-4">Adicionar Destinatários</h3>
                  
                  <div class="mb-4">
                    <div class="flex items-center gap-2 mb-3">
                      <button
                        @click="recipientTab = 'users'"
                        class="px-3 py-1.5 rounded-md text-sm transition-colors"
                        :class="{
                          'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400': recipientTab === 'users',
                          'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700': recipientTab !== 'users'
                        }"
                      >
                        <div class="flex items-center gap-1">
                          <UserIcon class="w-4 h-4" />
                          Usuários
                        </div>
                      </button>
                      <button
                        @click="recipientTab = 'groups'"
                        class="px-3 py-1.5 rounded-md text-sm transition-colors"
                        :class="{
                          'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400': recipientTab === 'groups',
                          'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700': recipientTab !== 'groups'
                        }"
                      >
                        <div class="flex items-center gap-1">
                          <UsersIcon class="w-4 h-4" />
                          Grupos
                        </div>
                      </button>
                    </div>
                    
                    <div class="relative mb-4">
                      <input
                        type="text"
                        v-model="recipientSearchQuery"
                        :placeholder="recipientTab === 'users' ? 'Buscar usuários...' : 'Buscar grupos...'"
                        class="w-full px-3 py-2 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                    
                    <!-- Users Tab -->
                    <div v-if="recipientTab === 'users'" class="space-y-2 max-h-64 overflow-y-auto">
                      <div 
                        v-for="user in filteredUsers" 
                        :key="user.id"
                        class="flex justify-between items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                      >
                        <div class="flex items-center gap-3">
                          <div
                            class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 bg-blue-500"
                          >
                            <UserIcon class="w-4 h-4" />
                          </div>
                          <div>
                            <div class="text-sm font-medium">{{ user.name }}</div>
                            <div class="text-xs text-gray-500 dark:text-gray-400">{{ user.email }}</div>
                          </div>
                        </div>
                        <button
                          @click="addRecipient(user, 'user')"
                          class="p-1 text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400"
                          :disabled="isRecipientAlreadyAdded(user.id, 'user')"
                          :class="{ 'opacity-50 cursor-not-allowed': isRecipientAlreadyAdded(user.id, 'user') }"
                          :title="isRecipientAlreadyAdded(user.id, 'user') ? 'Já adicionado' : 'Adicionar'"
                        >
                          <PlusCircleIcon v-if="!isRecipientAlreadyAdded(user.id, 'user')" class="w-5 h-5" />
                          <CheckIcon v-else class="w-5 h-5" />
                        </button>
                      </div>
                      <div v-if="filteredUsers.length === 0" class="text-center py-4 text-gray-500 dark:text-gray-400">
                        Nenhum usuário encontrado
                      </div>
                    </div>
                    
                    <!-- Groups Tab -->
                    <div v-if="recipientTab === 'groups'" class="space-y-2 max-h-64 overflow-y-auto">
                      <div 
                        v-for="group in filteredGroups" 
                        :key="group.id"
                        class="flex justify-between items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                      >
                        <div class="flex items-center gap-3">
                          <div
                            class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 bg-purple-500"
                          >
                            <UsersIcon class="w-4 h-4" />
                          </div>
                          <div>
                            <div class="text-sm font-medium">{{ group.name }}</div>
                            <div class="text-xs text-gray-500 dark:text-gray-400">{{ group.userCount }} usuários</div>
                          </div>
                        </div>
                        <button
                          @click="addRecipient(group, 'group')"
                          class="p-1 text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400"
                          :disabled="isRecipientAlreadyAdded(group.id, 'group')"
                          :class="{ 'opacity-50 cursor-not-allowed': isRecipientAlreadyAdded(group.id, 'group') }"
                          :title="isRecipientAlreadyAdded(group.id, 'group') ? 'Já adicionado' : 'Adicionar'"
                        >
                          <PlusCircleIcon v-if="!isRecipientAlreadyAdded(group.id, 'group')" class="w-5 h-5" />
                          <CheckIcon v-else class="w-5 h-5" />
                        </button>
                      </div>
                      <div v-if="filteredGroups.length === 0" class="text-center py-4 text-gray-500 dark:text-gray-400">
                        Nenhum grupo encontrado
                      </div>
                    </div>
                  </div>
                  
                  <div class="flex justify-end gap-3 mt-6">
                    <button
                      class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                      @click="showRecipientModal = false"
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useClinicStore } from '@/stores/settings/clinicStore';
const clinicStore = useClinicStore();
clinicStore.initialize();
import BaseTemplate from '../templates/BaseTemplate.vue';
import { 
  Plus, 
  ChevronDown,
  ChevronUp,
  Check, 
  Search, 
  Edit, 
  Trash, 
  Save, 
  ArrowLeft, 
  RefreshCw, 
  AlertTriangle,
  FileText,
  Bell,
  User,
  Users,
  Mail,
  Smartphone,
  MessageCircle,
  Clock,
  Send,
  X,
  PlusCircle,
  AlertCircle
} from 'lucide-vue-next';

// Icons
const PlusIcon = Plus;
const ChevronDownIcon = ChevronDown;
const ChevronUpIcon = ChevronUp;
const CheckIcon = Check;
const SearchIcon = Search;
const EditIcon = Edit;
const TrashIcon = Trash;
const SaveIcon = Save;
const ArrowLeftIcon = ArrowLeft;
const RefreshCwIcon = RefreshCw;
const AlertTriangleIcon = AlertTriangle;
const FileTextIcon = FileText;
const BellIcon = Bell;
const UserIcon = User;
const UsersIcon = Users;
const MailIcon = Mail;
const SmartphoneIcon = Smartphone;
const MessageCircleIcon = MessageCircle;
const ClockIcon = Clock;
const SendIcon = Send;
const XIcon = X;
const PlusCircleIcon = PlusCircle;
const AlertCircleIcon = AlertCircle;

// Estado
const currentView = ref('list'); // 'list' ou 'form'
const showSuccessModal = ref(false);
const showDeleteModal = ref(false);
const showTestModal = ref(false);
const showRecipientModal = ref(false);
const editMode = ref(false);
const notificationToDelete = ref(null);
const notificationToTest = ref(null);
const searchQuery = ref('');
const filterEvent = ref('');
const activeTab = ref('basic');
const expandedHistoryEntry = ref(null);
const recipientTab = ref('users');
const recipientSearchQuery = ref('');

// Teste de notificação
const testStatus = ref('preparing'); // 'preparing', 'loading', 'success', 'error'
const testEvent = ref('');
const testRecipient = ref('');
const testChannel = ref('');
const testResult = ref({});

// Definição das abas
const tabs = [
  { id: 'basic', name: 'Informações Básicas', icon: FileTextIcon },
  { id: 'events', name: 'Eventos', icon: BellIcon },
  { id: 'recipients', name: 'Destinatários', icon: UsersIcon },
  { id: 'content', name: 'Conteúdo', icon: MessageCircleIcon },
  { id: 'history', name: 'Histórico', icon: ClockIcon }
];

// Modelo de notificação vazio
const emptyNotification = {
  id: 'Automático',
  name: '',
  description: '',
  active: true,
  priority: 'normal',
  events: [],
  recipients: [],
  channels: ['app'],
  content: {
    subject: '',
    message: '',
    actionText: '',
    actionUrl: ''
  },
  history: []
};

// Notificação atual para edição/criação
const currentNotification = ref({ ...emptyNotification });

// Lista de notificações
const notifications = ref([
  {
    id: 'NOT0001',
    name: 'Consulta Agendada',
    description: 'Notifica quando uma nova consulta é agendada',
    active: true,
    priority: 'normal',
    events: ['appointment.created'],
    recipients: [
      { id: 'USR0001', type: 'user', name: 'Dr. Carlos Silva', email: 'carlos.silva@exemplo.com' },
      { id: 'GRP0001', type: 'group', name: 'Recepcionistas', userCount: 5 }
    ],
    channels: ['app', 'email'],
    content: {
      subject: 'Nova consulta agendada',
      message: 'Uma nova consulta foi agendada para o paciente {nome} no dia {data} às {hora}.',
      actionText: 'Ver Agenda',
      actionUrl: '/agenda/{data}'
    },
    history: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 5),
        event: 'appointment.created',
        status: 'delivered',
        channel: 'app',
        recipient: 'Dr. Carlos Silva',
        subject: 'Nova consulta agendada',
        message: 'Uma nova consulta foi agendada para o paciente João Silva no dia 15/08/2023 às 14:30.'
      },
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 30),
        event: 'appointment.created',
        status: 'delivered',
        channel: 'email',
        recipient: 'Dr. Carlos Silva',
        subject: 'Nova consulta agendada',
        message: 'Uma nova consulta foi agendada para o paciente Maria Oliveira no dia 16/08/2023 às 10:15.'
      }
    ]
  },
  {
    id: 'NOT0002',
    name: 'Lembrete de Consulta',
    description: 'Envia lembretes de consultas agendadas',
    active: true,
    priority: 'high',
    events: ['appointment.reminder'],
    recipients: [
      { id: 'GRP0002', type: 'group', name: 'Médicos', userCount: 8 }
    ],
    channels: ['app', 'email', 'sms'],
    content: {
      subject: 'Lembrete de consulta',
      message: 'Lembrete: Você tem uma consulta agendada com o paciente {nome} amanhã às {hora}.',
      actionText: 'Ver Detalhes',
      actionUrl: '/consultas/{id}'
    },
    history: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 120),
        event: 'appointment.reminder',
        status: 'delivered',
        channel: 'sms',
        recipient: 'Dr. Ana Souza',
        subject: 'Lembrete de consulta',
        message: 'Lembrete: Você tem uma consulta agendada com o paciente Pedro Santos amanhã às 09:00.'
      }
    ]
  },
  {
    id: 'NOT0003',
    name: 'Resultado de Exame',
    description: 'Notifica quando um resultado de exame está disponível',
    active: true,
    priority: 'urgent',
    events: ['exam.result'],
    recipients: [
      { id: 'USR0002', type: 'user', name: 'Dra. Ana Souza', email: 'ana.souza@exemplo.com' },
      { id: 'USR0003', type: 'user', name: 'Dr. Roberto Almeida', email: 'roberto.almeida@exemplo.com' }
    ],
    channels: ['app', 'email', 'whatsapp'],
    content: {
      subject: 'Resultado de exame disponível',
      message: 'O resultado do exame {tipo} do paciente {nome} está disponível para análise.',
      actionText: 'Ver Resultado',
      actionUrl: '/exames/{id}'
    },
    history: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3),
        event: 'exam.result',
        status: 'delivered',
        channel: 'app',
        recipient: 'Dra. Ana Souza',
        subject: 'Resultado de exame disponível',
        message: 'O resultado do exame Hemograma do paciente João Silva está disponível para análise.'
      },
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4),
        event: 'exam.result',
        status: 'failed',
        channel: 'whatsapp',
        recipient: 'Dr. Roberto Almeida',
        error: 'Número de telefone inválido',
        subject: 'Resultado de exame disponível',
        message: 'O resultado do exame Glicemia do paciente Maria Oliveira está disponível para análise.'
      }
    ]
  },
  {
    id: 'NOT0004',
    name: 'Fatura Gerada',
    description: 'Notifica quando uma nova fatura é gerada',
    active: false,
    priority: 'normal',
    events: ['invoice.generated'],
    recipients: [
      { id: 'GRP0003', type: 'group', name: 'Financeiro', userCount: 3 }
    ],
    channels: ['app', 'email'],
    content: {
      subject: 'Nova fatura gerada',
      message: 'Uma nova fatura no valor de R$ {valor} foi gerada para o paciente {nome}.',
      actionText: 'Ver Fatura',
      actionUrl: '/faturas/{id}'
    },
    history: []
  }
]);

// Usuários disponíveis para seleção
const users = ref([
  { id: 'USR0001', name: 'Dr. Carlos Silva', email: 'carlos.silva@exemplo.com', role: 'Médico' },
  { id: 'USR0002', name: 'Dra. Ana Souza', email: 'ana.souza@exemplo.com', role: 'Médico' },
  { id: 'USR0003', name: 'Dr. Roberto Almeida', email: 'roberto.almeida@exemplo.com', role: 'Médico' },
  { id: 'USR0004', name: 'Juliana Mendes', email: 'juliana.mendes@exemplo.com', role: 'Recepcionista' },
  { id: 'USR0005', name: 'Marcos Oliveira', email: 'marcos.oliveira@exemplo.com', role: 'Enfermeiro' },
  { id: 'USR0006', name: 'Carla Santos', email: 'carla.santos@exemplo.com', role: 'Administrador' },
  { id: 'USR0007', name: 'Paulo Ferreira', email: 'paulo.ferreira@exemplo.com', role: 'Financeiro' },
  { id: 'USR0008', name: 'Luciana Costa', email: 'luciana.costa@exemplo.com', role: 'Recepcionista' }
]);

// Grupos disponíveis para seleção
const groups = ref([
  { id: 'GRP0001', name: 'Recepcionistas', userCount: 5, description: 'Equipe de recepção' },
  { id: 'GRP0002', name: 'Médicos', userCount: 8, description: 'Todos os médicos' },
  { id: 'GRP0003', name: 'Financeiro', userCount: 3, description: 'Equipe financeira' },
  { id: 'GRP0004', name: 'Enfermagem', userCount: 6, description: 'Equipe de enfermagem' },
  { id: 'GRP0005', name: 'Administração', userCount: 4, description: 'Equipe administrativa' }
]);

// Usuários disponíveis para teste
const availableTestRecipients = ref([
  { id: 'TEST001', name: 'Usuário de Teste', email: 'teste@exemplo.com' },
  { id: 'TEST002', name: 'Administrador', email: 'admin@exemplo.com' }
]);

// Computed properties
const filteredNotifications = computed(() => {
  let result = [...notifications.value];
  
  // Filtrar por termo de busca
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(notification => 
      notification.name.toLowerCase().includes(query) ||
      notification.id.toLowerCase().includes(query) ||
      notification.description.toLowerCase().includes(query)
    );
  }
  
  // Filtrar por evento
  if (filterEvent.value) {
    result = result.filter(notification => notification.events.includes(filterEvent.value));
  }
  
  return result;
});

const isFormValid = computed(() => {
  return (
    currentNotification.value.name.trim() !== '' &&
    currentNotification.value.events.length > 0 &&
    currentNotification.value.recipients.length > 0 &&
    currentNotification.value.channels.length > 0 &&
    currentNotification.value.content.subject.trim() !== '' &&
    currentNotification.value.content.message.trim() !== ''
  );
});

const filteredUsers = computed(() => {
  if (!recipientSearchQuery.value) {
    return users.value;
  }
  
  const query = recipientSearchQuery.value.toLowerCase();
  return users.value.filter(user => 
    user.name.toLowerCase().includes(query) ||
    user.email.toLowerCase().includes(query) ||
    user.role.toLowerCase().includes(query)
  );
});

const filteredGroups = computed(() => {
  if (!recipientSearchQuery.value) {
    return groups.value;
  }
  
  const query = recipientSearchQuery.value.toLowerCase();
  return groups.value.filter(group => 
    group.name.toLowerCase().includes(query) ||
    group.description.toLowerCase().includes(query)
  );
});

// Métodos
function openNewNotificationForm() {
  currentNotification.value = JSON.parse(JSON.stringify(emptyNotification));
  editMode.value = false;
  activeTab.value = 'basic'; // Reset to first tab
  currentView.value = 'form';
}

function editNotification(notification) {
  currentNotification.value = JSON.parse(JSON.stringify(notification)); // Deep copy
  editMode.value = true;
  activeTab.value = 'basic'; // Reset to first tab
  currentView.value = 'form';
}

function goToList() {
  currentView.value = 'list';
}

function saveNotification() {
  if (!isFormValid.value) return;
  
  if (editMode.value) {
    // Atualizar notificação existente
    const index = notifications.value.findIndex(n => n.id === currentNotification.value.id);
    if (index !== -1) {
      notifications.value[index] = JSON.parse(JSON.stringify(currentNotification.value));
    }
  } else {
    // Criar nova notificação
    const newNotification = JSON.parse(JSON.stringify(currentNotification.value));
    newNotification.id = generateNotificationId();
    // Adicionar histórico vazio
    newNotification.history = [];
    notifications.value.push(newNotification);
  }
  
  // Mostrar modal de sucesso
  showSuccessModal.value = true;
  
  // Em um caso real, você enviaria os dados para uma API
  console.log('Notificação salva:', currentNotification.value);
}

function closeSuccessModal() {
  showSuccessModal.value = false;
  currentView.value = 'list';
}

function confirmDeleteNotification(notification) {
  notificationToDelete.value = notification;
  showDeleteModal.value = true;
}

function deleteNotification() {
  if (!notificationToDelete.value) return;
  
  const index = notifications.value.findIndex(n => n.id === notificationToDelete.value.id);
  if (index !== -1) {
    notifications.value.splice(index, 1);
  }
  
  showDeleteModal.value = false;
  notificationToDelete.value = null;
}

function resetFilters() {
  searchQuery.value = '';
  filterEvent.value = '';
}

function generateNotificationId() {
  // Simulando a geração de um ID de notificação
  const prefix = 'NOT';
  const lastId = notifications.value.length > 0 
    ? parseInt(notifications.value[notifications.value.length - 1].id.replace('NOT', '')) 
    : 0;
  const newId = (lastId + 1).toString().padStart(4, '0');
  return `${prefix}${newId}`;
}

function getNotificationColor(id) {
  const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4', '#84cc16'];
  const index = parseInt(id.replace(/\D/g, '')) % colors.length;
  return colors[index];
}

function getEventLabel(eventCode) {
  const eventLabels = {
    'patient.created': 'Paciente Cadastrado',
    'patient.updated': 'Paciente Atualizado',
    'appointment.created': 'Consulta Agendada',
    'appointment.updated': 'Consulta Atualizada',
    'appointment.canceled': 'Consulta Cancelada',
    'appointment.reminder': 'Lembrete de Consulta',
    'exam.result': 'Resultado de Exame',
    'exam.scheduled': 'Exame Agendado',
    'invoice.generated': 'Fatura Gerada',
    'invoice.overdue': 'Fatura Vencida',
    'payment.received': 'Pagamento Recebido'
  };
  
  return eventLabels[eventCode] || eventCode;
}

function getChannelLabel(channelCode) {
  const channelLabels = {
    'app': 'Notificação no App',
    'email': 'Email',
    'sms': 'SMS',
    'whatsapp': 'WhatsApp'
  };
  
  return channelLabels[channelCode] || channelCode;
}

function getStatusLabel(statusCode) {
  const statusLabels = {
    'delivered': 'Entregue',
    'pending': 'Pendente',
    'failed': 'Falhou'
  };
  
  return statusLabels[statusCode] || statusCode;
}

function formatDate(date) {
  if (!date) return '';
  
  const d = new Date(date);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function selectAllEvents() {
  currentNotification.value.events = [
    'patient.created', 'patient.updated',
    'appointment.created', 'appointment.updated', 'appointment.canceled', 'appointment.reminder',
    'exam.result', 'exam.scheduled',
    'invoice.generated', 'invoice.overdue', 'payment.received'
  ];
}

function deselectAllEvents() {
  currentNotification.value.events = [];
}

function toggleHistoryEntry(index) {
  expandedHistoryEntry.value = expandedHistoryEntry.value === index ? null : index;
}

function clearHistory() {
  if (currentNotification.value && currentNotification.value.history) {
    currentNotification.value.history = [];
  }
}

function testNotification(notification) {
  notificationToTest.value = JSON.parse(JSON.stringify(notification));
  testEvent.value = notification.events.length > 0 ? notification.events[0] : '';
  testRecipient.value = availableTestRecipients.value[0].id;
  testChannel.value = notification.channels.length > 0 ? notification.channels[0] : 'app';
  testStatus.value = 'preparing';
  showTestModal.value = true;
}

function executeTestNotification() {
  // Em um caso real, você enviaria uma requisição HTTP
  testStatus.value = 'loading';
  
  // Simulação de envio
  setTimeout(() => {
    // Simulando sucesso ou erro aleatoriamente
    const success = Math.random() > 0.2;
    
    if (success) {
      testStatus.value = 'success';
      testResult.value = {
        status: 'delivered'
      };
      
      // Adicionar ao histórico
      const historyEntry = {
        timestamp: new Date(),
        event: testEvent.value,
        status: 'delivered',
        channel: testChannel.value,
        recipient: getTestRecipientName(),
        subject: notificationToTest.value.content.subject,
        message: notificationToTest.value.content.message
      };
      
      // Se a notificação sendo testada for a atual em edição, adiciona ao histórico
      if (editMode.value && currentNotification.value.id === notificationToTest.value.id) {
        if (!currentNotification.value.history) {
          currentNotification.value.history = [];
        }
        currentNotification.value.history.unshift(historyEntry);
      }
      
      // Adiciona ao histórico da notificação na lista principal
      const index = notifications.value.findIndex(n => n.id === notificationToTest.value.id);
      if (index !== -1) {
        if (!notifications.value[index].history) {
          notifications.value[index].history = [];
        }
        notifications.value[index].history.unshift(historyEntry);
      }
    } else {
      testStatus.value = 'error';
      testResult.value = {
        status: 'failed',
        error: Math.random() > 0.5 ? "Falha na entrega da notificação" : "Destinatário não encontrado"
      };
      
      // Adicionar ao histórico
      const historyEntry = {
        timestamp: new Date(),
        event: testEvent.value,
        status: 'failed',
        channel: testChannel.value,
        recipient: getTestRecipientName(),
        error: testResult.value.error,
        subject: notificationToTest.value.content.subject,
        message: notificationToTest.value.content.message
      };
      
      // Se a notificação sendo testada for a atual em edição, adiciona ao histórico
      if (editMode.value && currentNotification.value.id === notificationToTest.value.id) {
        if (!currentNotification.value.history) {
          currentNotification.value.history = [];
        }
        currentNotification.value.history.unshift(historyEntry);
      }
      
      // Adiciona ao histórico da notificação na lista principal
      const index = notifications.value.findIndex(n => n.id === notificationToTest.value.id);
      if (index !== -1) {
        if (!notifications.value[index].history) {
          notifications.value[index].history = [];
        }
        notifications.value[index].history.unshift(historyEntry);
      }
    }
  }, 1500);
}

function getTestRecipientName() {
  const recipient = availableTestRecipients.value.find(r => r.id === testRecipient.value);
  return recipient ? recipient.name : 'Usuário de Teste';
}

function addRecipient(item, type) {
  // Verificar se já existe
  if (isRecipientAlreadyAdded(item.id, type)) {
    return;
  }
  
  // Adicionar ao array de destinatários
  currentNotification.value.recipients.push({
    id: item.id,
    type: type,
    name: item.name,
    email: item.email || undefined,
    userCount: item.userCount || undefined
  });
}

function removeRecipient(index) {
  currentNotification.value.recipients.splice(index, 1);
}

function isRecipientAlreadyAdded(id, type) {
  return currentNotification.value.recipients.some(r => r.id === id && r.type === type);
}

// Lifecycle hooks
onMounted(() => {
  // Inicializar a visualização
  currentView.value = 'list';
});
</script>

<style scoped>
.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Transições suaves */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Ajustes para melhorar a responsividade */
@media (max-width: 640px) {
  .text-xl {
    font-size: 1.1rem;
  }
}

.clinic-btn {
  background: var(--clinic-btn-bg) !important;
  color: #fff !important;
  transition: background 0.2s;
}
.clinic-btn:hover, .clinic-btn:focus {
  background: var(--clinic-btn-bg-hover) !important;
}
.clinic-tab-active {
  color: var(--clinic-icon-color) !important;
  border-bottom: 2px solid var(--clinic-icon-color) !important;
}
.clinic-tab-inactive {
  color: var(--clinic-font-color) !important;
}
.clinic-header {
  color: var(--clinic-font-color) !important;
}
</style>